/**
 * "Draw on Path" API + interactive point editor for fabric.BoundedText.
 * Mixed onto fabric.BoundedText.prototype (see the fabric.util.object.extend() call at the
 * bottom) instead of living inline in BoundedText.js, same pattern TextTransform/Control.js
 * uses for fabric.TextTransform. Must be required AFTER BoundedText.js so fabric.BoundedText
 * already exists (BoundedText.js itself requires this file at the bottom, once its class is
 * defined - see the comment there).
 */
const { normalizePathToBounds, parsePathToCommands } = require('./TextPath');

/**
 * Starter shapes offered in the "Draw on Path" preset picker. Coordinates are in an arbitrary
 * nominal space - setTextPath() normalizes them (by their own bounding box) into the size-
 * independent unit path used everywhere else, so the exact numbers here only matter relative
 * to each other, not in absolute terms.
 */
const TEXT_PATH_PRESETS = {
    straight: 'M -150,0 L 150,0',
    // Diagonal straight line (bottom-left to top-right), distinct from "straight" (horizontal)
    line: 'M -150,80 L 150,-80',
    arc: 'M -150,60 Q 0,-160 150,60',
    angle: 'M -150,60 L 0,-100 L 150,60',
    // Cubic bezier (C x1 y1, x2 y2, x y), with two independent control points instead of Q's
    //  single shared one
    wave: 'M -150,60 C 10,-100 50,60 150,60'
};

// normalizePathToBounds() fits the path's own bounding box to EXACTLY the target box, so the
//  curve itself can touch the object's edges. Since text sits ON the path with its own height
//  (ascent/descent) extending past it, a path that hugs the box edges makes the text poke out
//  and get clipped. Normalizing against less than the full box leaves headroom on every side.
const TEXT_PATH_NORMALIZE_MARGIN = 0.7;

// How far past the object's own bounding box a point can be dragged in enableTextPathEditor(),
//  as a fraction of the box's half-width/half-height (0 = hard-clamped exactly to the box edge,
//  0.15 = can go 15% further out on each side). A little slack keeps the editor from feeling
//  stuck right at the edge while still bounding how far glyphs can paint outside
//  _getCacheCanvasDimensions()'s margin.
const TEXT_PATH_EDITOR_BOUNDS_MARGIN = 0.15;

const textPathEditor = {
    /**
     * "Draw on Path". Pass an SVG path 'd' string (in the same coordinate space as the text
     * item, ie. centered around 0,0) to make the text follow that path instead of flowing
     * horizontally. Pass a falsy value to go back to normal rendering.
     *
     * @param {string} pathData SVG path 'd' attribute, or falsy to disable path mode
     * @param {boolean} [autoFit=true] if true, scales the text so it spans the full path length;
     *   if false, the text is drawn at its natural size (may overflow or fall short of the path)
     * @param {boolean} [debugPath=false] if true, strokes the path in red (dashed) with a green
     *   dot at its starting point, to see exactly where the path sits while tuning it by hand -
     *   development-only aid, not meant to be a persisted/user-facing option
     *
     * Examples (from the browser console, with a BoundedText selected):
     *   canvas.getActiveObject().setTextPath('M -140,60 Q 0,-160 140,60')             // autoFit on
     *   canvas.getActiveObject().setTextPath('M -140,60 Q 0,-160 140,60', false)      // autoFit off
     *   canvas.getActiveObject().setTextPath('M -140,60 Q 0,-160 140,60', true, true) // + path outline
     *   canvas.getActiveObject().setTextPath(null)                                    // back to normal
     *
     * @param {Object} [options]
     * @param {boolean} [options.normalize=true] scale+center `pathData` (by its own bounding box)
     *   into a size-independent "unit path" whose own bbox fits inside a `margin` x `margin` box -
     *   so an arbitrarily-authored path always lines up with the BoundedText regardless of the
     *   coordinates it was drawn with, AND automatically follows the object when it's resized
     *   (OpentypeIText._renderTextAlongPath scales this unit path back up by the object's live
     *   width/height on every render - see pathScale there). This is baked into the stored path
     *   once, here; it is NOT re-derived from the object's size on every frame, because doing
     *   that while dragging a point in enableTextPathEditor() would make the whole curve rescale/
     *   recenter every frame (the bounding box changes as you drag). Pass `false` when you
     *   already have unit-space coordinates (the path editor does this).
     * @param {number} [options.margin=1] fraction (0-1] of the unit box the path's own bounding
     *   box gets normalized into (see TEXT_PATH_NORMALIZE_MARGIN above for why less than 1 leaves
     *   room for the text's own height around the curve). Defaults to 1 (no margin/full box) -
     *   NOT the shrunk margin - on purpose: this default gets applied whenever `pathData` is set
     *   without explicitly asking for a margin (eg. restoring a previously-saved textPath), so a
     *   future tweak to TEXT_PATH_NORMALIZE_MARGIN can't silently reshuffle already-saved designs
     *   the next time they're loaded. setTextPathPreset() is the one place that opts into it.
     */
    setTextPath(pathData, autoFit = true, debugPath = false, { normalize = true, margin = 1 } = {}) {
        let finalPathData = pathData || null;
        if (finalPathData && normalize) {
            const commands = parsePathToCommands(finalPathData);
            const normalized = normalizePathToBounds(commands, margin, margin);
            finalPathData = fabric.util.joinPath(normalized);
        }

        // "Draw on Path" and "Allow Moving Text" (allowEdit - see TextAdvancedProperties.vue) are
        //  mutually exclusive: allowEdit's whole model (bounding-box growth/recenter, see
        //  OpentypeIText.js's _getBoundedTextMetrics/_constraintScaleByProjection) assumes a
        //  rectangular box that grows with the text, which doesn't apply once the text follows a
        //  curve instead. Force it off the moment a path is actually set (not on every no-op
        //  call - eg. every frame while dragging a point in the editor - hence the guard), so the
        //  two features can never end up simultaneously enabled on the same element.
        if (finalPathData && (this.allowEdit || this.oldAllowEdit)) {
            this.setAllowEdit(false);
            this.set('oldAllowEdit', false);
            this.resetEditMode?.();
        }

        this.item(0).textPath = finalPathData;
        this.item(0).textPathAutoFit = autoFit;
        this.item(0)._textPathDebug = debugPath;
        // Mirrored onto the group itself too, so UI code can read element.textPath directly
        //  instead of reaching into element.item(0) - rendering only ever reads from item(0).
        this.textPath = finalPathData;
        this.textPathAutoFit = autoFit;
        this.textPathDebug = debugPath;
        this.item(0).set('dirty', true);
        this.set('dirty', true);
        if (this.canvas) {
            this.canvas.requestRenderAll();
        }
        // this.editMode mirrors $scope.editMode at the moment this object was loaded/created
        //  (see loadText() in createStepOneCtrl.js) - only mark textPath/textPathAutoFit as
        //  instance-edited (used in step 2 to keep this product's own path instead of silently
        //  following template updates - see productService.js's editedProps handling) while
        //  actually editing an existing product instance, not during template authoring itself
        //  or while merely loading/cloning an object that already has a saved path.
        if (this.editMode) {
            this.setEditedProp?.('textPath', 'textPathAutoFit');
        }
    },

    /**
     * Toggles autoFit without touching the path itself (no re-normalize) - lighter-weight than
     * calling setTextPath() again with the current path data.
     * @param {boolean} autoFit
     */
    setTextPathAutoFit(autoFit) {
        this.item(0).textPathAutoFit = autoFit;
        this.textPathAutoFit = autoFit;
        this.item(0).set('dirty', true);
        this.set('dirty', true);
        if (this.canvas) {
            this.canvas.requestRenderAll();
        }
        if (this.editMode) {
            this.setEditedProp?.('textPathAutoFit');
        }
    },

    /**
     * Toggles the red dashed path outline (development aid, see debugDrawPath() in TextPath.js)
     * without touching the path itself.
     * @param {boolean} debugPath
     */
    setTextPathDebug(debugPath) {
        this.item(0)._textPathDebug = debugPath;
        this.textPathDebug = debugPath;
        this.item(0).set('dirty', true);
        this.set('dirty', true);
        if (this.canvas) {
            this.canvas.requestRenderAll();
        }
    },

    /**
     * Applies one of the built-in starter shapes (see TEXT_PATH_PRESETS above) - this is what
     * the "Draw on Path" preset picker buttons call. Preserves the current autoFit/debug settings
     * (or their defaults for a brand new path) so switching presets doesn't reset them.
     * @param {'straight'|'arc'|'angle'|'wave'} presetKey
     */
    setTextPathPreset(presetKey) {
        const pathData = TEXT_PATH_PRESETS[presetKey];
        if (!pathData) {
            console.warn('Unknown text path preset: ' + presetKey);
            return;
        }
        this.setTextPath(pathData, this.item(0).textPathAutoFit !== false, !!this.item(0)._textPathDebug, {
            margin: TEXT_PATH_NORMALIZE_MARGIN
        });

        // The point editor's controls (_textPathEditorCommands) are a snapshot taken when
        //  enableTextPathEditor() was called - they don't know the path just changed underneath
        //  them. Re-running it (safe/idempotent - see its own comment about being called again
        //  while already editing) rebuilds the handles to match the new preset. Only done here,
        //  NOT inside setTextPath() itself, because the point-drag actionHandler also calls
        //  setTextPath() on every mouse-move frame - rebuilding the whole controls set there
        //  would swap out the very control the user is mid-drag on.
        if (this.isEditingTextPath) {
            this.enableTextPathEditor();
        }
    },

    /**
     * "Draw on Path" editor. Adds one draggable fabric.Control per path anchor/control
     * point (green dots for M, blue dots for the rest) on top of the BoundedText, so the curve
     * can be reshaped by hand instead of hand-editing the SVG 'd' string. Requires setTextPath()
     * to have been called first. Call disableTextPathEditor() to go back to the normal corner/rotate
     * controls.
     *
     * Example (from the browser console):
     *   canvas.getActiveObject().setTextPath('M -140,60 Q 0,-160 140,60');
     *   canvas.getActiveObject().enableTextPathEditor();
     */
    enableTextPathEditor() {
        if (!this.item(0).textPath) {
            console.warn('setTextPath() must be called with a path before enableTextPathEditor()');
            return;
        }
        if (!this.canvas) {
            return;
        }

        // textPath is stored as a size-independent "unit path" (see setTextPath()'s comment) -
        //  _textPathEditorCommands keeps that same unit space so it stays valid across resizes;
        //  each control converts to/from local pixels using the object's live pathScale below.
        this._textPathEditorCommands = parsePathToCommands(this.item(0).textPath);
        // Only capture the true original controls once. Calling enableTextPathEditor() again while
        //  already editing (eg. after setting a new path without disabling first) must rebuild
        //  the pathPoint controls without clobbering _textPathEditorOriginalControls with the
        //  editor's OWN control set - otherwise disableTextPathEditor() would "restore" stale
        //  pathPoint controls instead of the real tl/tr/etc, while _textPathEditorCommands gets
        //  nulled out, crashing the next render.
        if (!this._textPathEditorOriginalControls) {
            this._textPathEditorOriginalControls = this.controls;
        }

        const editorControls = {};

        // Draws thin dashed guide lines between each bezier control point and the anchor it
        //  pulls on (prevAnchor<->control1 and control2/control<->ownAnchor), same idea as
        //  WarpFormControl.line in WarpForm.js: a non-interactive "control" whose only job is
        //  to paint something. Added first so forEachControl (insertion-ordered) draws it
        //  before the point dots, keeping the dots on top.
        editorControls.pathGuideLines = new fabric.Control({
            skipTargetFind: true,
            render: function (ctx, left, top, styleOverride, fabricObject) {
                const commands = fabricObject._textPathEditorCommands;
                if (!commands) {
                    return;
                }
                const pathScale = Math.min(fabricObject.item(0).maxWidth, fabricObject.item(0).maxHeight) || 1;
                const canvasMatrix = fabric.util.multiplyTransformMatrices(
                    fabricObject.canvas.viewportTransform,
                    fabricObject.calcTransformMatrix()
                );
                const toScreen = (x, y) =>
                    fabric.util.transformPoint({ x: x * pathScale, y: y * pathScale }, canvasMatrix);
                const drawLine = (p1, p2) => {
                    if (!p1 || !p2) {
                        return;
                    }
                    ctx.moveTo(p1.x, p1.y);
                    ctx.lineTo(p2.x, p2.y);
                };

                ctx.save();
                ctx.strokeStyle = '#5181FF';
                ctx.lineWidth = 1;
                ctx.setLineDash([6, 4]);
                ctx.beginPath();

                let prevAnchor = null;
                commands.forEach((command) => {
                    const type = command[0];
                    if (type === 'M' || type === 'L') {
                        prevAnchor = toScreen(command[1], command[2]);
                    } else if (type === 'Q') {
                        const control = toScreen(command[1], command[2]);
                        const anchor = toScreen(command[3], command[4]);
                        drawLine(prevAnchor, control);
                        drawLine(anchor, control);
                        prevAnchor = anchor;
                    } else if (type === 'C') {
                        const control1 = toScreen(command[1], command[2]);
                        const control2 = toScreen(command[3], command[4]);
                        const anchor = toScreen(command[5], command[6]);
                        drawLine(prevAnchor, control1);
                        drawLine(anchor, control2);
                        prevAnchor = anchor;
                    }
                    // 'Z' has no coordinates and doesn't change prevAnchor
                });

                ctx.stroke();
                ctx.restore();
            }
        });

        let controlIndex = 0;
        this._textPathEditorCommands.forEach((command, cmdIndex) => {
            // Z has no coordinates to edit
            for (let argIndex = 1; argIndex < command.length; argIndex += 2) {
                // M's only point, and the last point of L/C/Q, are the segment's anchor (on-curve)
                //  point; anything in between is a bezier control (off-curve) point - color-coded
                //  so it's clear at a glance which dots actually sit on the curve
                const isAnchor = command[0] === 'M' || argIndex === command.length - 2;
                editorControls['pathPoint' + controlIndex++] = new fabric.Control({
                    cmdIndex,
                    argIndex,
                    isAnchor,
                    cursorStyle: 'move',
                    actionName: 'modifyTextPath',
                    render: function (ctx, left, top) {
                        ctx.save();
                        ctx.fillStyle = isAnchor ? '#E863F4' : '#E863F4';
                        ctx.strokeStyle = '#ffffff';
                        ctx.lineWidth = 1.5;
                        ctx.beginPath();
                        ctx.arc(left, top, 8, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.stroke();
                        ctx.restore();
                    },
                    positionHandler: function (dim, finalMatrix, fabricObject) {
                        // Called as control.positionHandler(...), so `this` is the control
                        //  instance itself (this.cmdIndex/this.argIndex) - fabric does NOT pass
                        //  the control as a 4th argument in this version
                        if (
                            !fabricObject._textPathEditorCommands ||
                            !fabricObject._textPathEditorCommands[this.cmdIndex]
                        ) {
                            // Defensive: state got out of sync (eg. leftover controls after a
                            //  disableTextPathEditor() that couldn't find the real original controls
                            //  to restore) - park the handle at the object's center instead of
                            //  crashing the whole canvas render
                            return fabric.util.transformPoint({ x: 0, y: 0 }, fabricObject.calcTransformMatrix());
                        }
                        const pathScale = Math.min(fabricObject.item(0).maxWidth, fabricObject.item(0).maxHeight) || 1;
                        const point = {
                            x: fabricObject._textPathEditorCommands[this.cmdIndex][this.argIndex] * pathScale,
                            y: fabricObject._textPathEditorCommands[this.cmdIndex][this.argIndex + 1] * pathScale
                        };
                        // calcTransformMatrix() maps the object's local space to the canvas PLANE
                        //  (unzoomed/unpanned) - controls are drawn in screen/viewport space, so
                        //  the canvas's own viewportTransform (zoom + pan) has to be applied too,
                        //  same as WarpForm.js's WarpFormControl.point does via applyCanvasTransform()
                        const canvasMatrix = fabric.util.multiplyTransformMatrices(
                            fabricObject.canvas.viewportTransform,
                            fabricObject.calcTransformMatrix()
                        );
                        return fabric.util.transformPoint(point, canvasMatrix);
                    },
                    actionHandler: function (eventData, transform, x, y) {
                        const target = transform.target;
                        const control = target.controls[transform.corner];
                        if (!target._textPathEditorCommands || !target._textPathEditorCommands[control.cmdIndex]) {
                            return false;
                        }
                        const inverted = fabric.util.invertTransform(target.calcTransformMatrix());
                        const localPoint = fabric.util.transformPoint({ x, y }, inverted);
                        const pathScale = Math.min(target.item(0).maxWidth, target.item(0).maxHeight) || 1;

                        // Anchor (on-curve) points are tightly clamped to the object's own
                        //  bounding box - glyphs sit ON the anchors, so bounding those bounds the
                        //  ink. Bezier control points are off-curve steering handles that never
                        //  get inked directly, so they get more slack to shape the curve near the
                        //  edges - but not unlimited: they're bounded to the same local-space
                        //  extent _getCacheCanvasDimensions() actually allocates (this.width/
                        //  height * 2, see that method), so a control point can never push the
                        //  curve (and therefore where an anchor riding it could later land) past
                        //  what the cache canvas can hold without clipping.
                        const halfWidth = control.isAnchor
                            ? (target.item(0).maxWidth / 2) * (1 + TEXT_PATH_EDITOR_BOUNDS_MARGIN)
                            : target.width;
                        const halfHeight = control.isAnchor
                            ? (target.item(0).maxHeight / 2) * (1 + TEXT_PATH_EDITOR_BOUNDS_MARGIN)
                            : target.height;
                        const finalX = Math.min(Math.max(localPoint.x, -halfWidth), halfWidth);
                        const finalY = Math.min(Math.max(localPoint.y, -halfHeight), halfHeight);

                        target._textPathEditorCommands[control.cmdIndex][control.argIndex] = finalX / pathScale;
                        target._textPathEditorCommands[control.cmdIndex][control.argIndex + 1] = finalY / pathScale;

                        const newPathData = fabric.util.joinPath(target._textPathEditorCommands);
                        // normalize:false - the points are already in local space, re-normalizing
                        //  here would rescale/recenter the whole curve on every drag frame
                        target.setTextPath(newPathData, target.item(0).textPathAutoFit, target.item(0)._textPathDebug, {
                            normalize: false
                        });
                        return true;
                    }
                });
            }
        });

        this.controls = editorControls;
        this.isEditingTextPath = true;
        // Auto-enable the debug path outline while actively editing points - seeing the curve
        //  itself (not just the text riding on it) makes it much easier to tell what a drag is
        //  actually doing, especially once the text no longer traces the path closely (eg.
        //  autoFit off, or a short text on a long path).
        this.setTextPathDebug(true);
        this.set('dirty', true);
        this.canvas.setActiveObject(this);
        this.canvas.requestRenderAll();
    },

    disableTextPathEditor() {
        if (this._textPathEditorOriginalControls) {
            this.controls = this._textPathEditorOriginalControls;
            this._textPathEditorOriginalControls = null;
        }
        this._textPathEditorCommands = null;
        this.isEditingTextPath = false;
        // Turn the debug outline back off with the editor - it's an editing aid, not something
        //  that should stay visible once you're done reshaping the curve.
        this.setTextPathDebug(false);
        this.item(0).set('dirty', true);
        this.set('dirty', true);
        if (this.canvas) {
            this.canvas.requestRenderAll();
        }
    },

    /**
     * Convenience toggle for a single UI button - mirrors fabric.TextTransform's toggleEdit()/
     * editWarpShape() pattern used by the equivalent "Edit" pencil button for warp shapes.
     */
    toggleTextPathEditor() {
        if (this.isEditingTextPath) {
            this.disableTextPathEditor();
        } else {
            this.enableTextPathEditor();
        }
    }
};

fabric.util.object.extend(fabric.BoundedText.prototype, textPathEditor);
